package pl.decks.personal.pages.ievp.sections.background

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.icons.fa.FaAt
import com.varabyte.kobweb.silk.components.icons.fa.FaCalendar
import com.varabyte.kobweb.silk.components.icons.fa.FaChevronDown
import com.varabyte.kobweb.silk.components.icons.fa.FaMapPin
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.browser.window
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import pl.decks.personal.components.widgets.LeadingIconRow
import pl.decks.personal.components.widgets.TrailingIconRow
import pl.decks.personal.pages.ievp.IEVPJourney
import pl.decks.personal.pages.ievp.sections.background.BackgroundSectionStylesheet.Proof
import pl.decks.personal.styles.SiteLayout
import pl.decks.posthog.client.PostHogClient

private enum class BackgroundEntryProgressDirection { FORWARD, FULL, BACKWARD, ; }

private data class ProgressSegment(
    val start: Float,
    val end: Float,
    val entry: BackgroundEntry,
    val direction: BackgroundEntryProgressDirection,
)

@Composable
fun BackgroundSectionContent(sectionProgress: Float) {
    val bufferVH = 100f
    val stepVH = 40f
    val progressSegments = remember { buildSegmentsProgress(stepVH, keepFullVH = bufferVH) }
    val totalHeight = 2 * bufferVH + progressSegments.last().end
    val currentVH = sectionProgress * totalHeight - bufferVH
    val currentSegment = progressSegments.firstOrNull { currentVH in it.start..it.end }

    LaunchedEffect(currentSegment) {
        if (currentSegment != null && currentSegment.direction == BackgroundEntryProgressDirection.FULL)
            PostHogClient.capture(IEVPJourney.BackgroundCompleted(currentSegment.entry))
    }

    Box(
        BackgroundSectionStylesheet.Section.toModifier().height((totalHeight).vh)
    ) {
        Column(
            BackgroundSectionStylesheet.StickyCanvas.toModifier(),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            currentSegment?.let { seg ->
                val segmentProgress = (currentVH - seg.start).div(seg.end - seg.start).coerceIn(0f, 1f)
                BackgroundFacetDraw(
                    seg.entry,
                    drawProgress = when (seg.direction) {
                        BackgroundEntryProgressDirection.FORWARD -> segmentProgress
                        BackgroundEntryProgressDirection.FULL -> 1f
                        BackgroundEntryProgressDirection.BACKWARD -> 1f - segmentProgress
                    },
                    modifier = Modifier.weight(1f).fillMaxHeight()
                )
                BackgroundFacetScrollPrompt()
            }
        }
    }
}

@OptIn(DelicateApi::class)
@Composable
private fun BackgroundFacetDraw(
    entry: BackgroundEntry,
    drawProgress: Float,
    modifier: Modifier = Modifier,
) = Column(BackgroundSectionStylesheet.Facet.Container.toModifier().then(modifier)) {
    val stepSize = 1f / entry.revealSteps
    fun linearReveal(atStep: Int) = ((drawProgress - atStep * stepSize) / stepSize).coerceIn(0f, 1f)

    val breakpoint = rememberBreakpoint()
    Column(
        modifier = BackgroundSectionStylesheet.Facet.RoleHeading.toModifier(),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        if (breakpoint >= Breakpoint.MD || window.innerHeight >= 780) {
            BackgroundFacetMainIcon(progress = linearReveal(0)) { entry.icon() }
        }
        BackgroundFacetTitle(entry.title, progress = linearReveal(0))
    }

    BackgroundFacetFlavour(entry.flavorText, progress = linearReveal(1))

    Column(Proof.EntryList.toModifier()) {
        entry.proofs.forEachIndexed { index, proof ->
            BackgroundFacetProof(
                proof,
                progress = linearReveal(2 + index),
                isLeftLayout = index % 2 == 0,
            )
        }
    }
}


@Composable
private fun BackgroundFacetMainIcon(
    progress: Float,
    icon: @Composable () -> Unit,
) = Box(
    modifier = BackgroundSectionStylesheet.Facet.IconHolder.toModifier(),
    contentAlignment = Alignment.Center,
) {
    Box(
        modifier = BackgroundSectionStylesheet.Facet.Icon.toModifier()
            .size(progress.times(100).percent),
        contentAlignment = Alignment.Center,
    ) {
        icon()
    }
}

@Composable
private fun BackgroundFacetTitle(
    title: String,
    progress: Float,
) = SpanText(
    title,
    modifier = BackgroundSectionStylesheet.Facet.Title.toModifier()
        .opacity(progress)
        .translateY((1f - progress).times(100).percent),
)

@Composable
private fun BackgroundFacetFlavour(
    text: String,
    progress: Float,
) = SpanText(
    text,
    modifier = BackgroundSectionStylesheet.Facet.Flavour.toModifier()
        .opacity(progress)
        .translateY((1f - progress).times(100).percent),
)

@Composable
private fun BackgroundFacetProof(
    proof: BackgroundEntry.Proof,
    progress: Float,
    isLeftLayout: Boolean,
) = Column(Proof.Entry.toModifier()) {
    val stepSize = 1f / 3
    fun linearReveal(atStep: Int) = ((progress - atStep * stepSize) / stepSize).coerceIn(0f, 1f)

    BackgroundEntryProofHeader(proof, progress = linearReveal(0), isLeftLayout = isLeftLayout)
    if (proof.timeSpan != null || proof.location != null) {
        BackgroundEntryProofSecondaryInfo(proof, progress = linearReveal(1))
    }
    proof.description?.let { BackgroundEntryProofDescription(it, progress = linearReveal(2)) }
}

@Composable
private fun BackgroundEntryProofHeader(
    proof: BackgroundEntry.Proof,
    progress: Float,
    isLeftLayout: Boolean,
) = Row(
    modifier = Modifier.fillMaxWidth()
        .opacity(progress)
        .flexDirection(if (isLeftLayout) FlexDirection.Row else FlexDirection.RowReverse),
    verticalAlignment = Alignment.CenterVertically,
) {
    val layMul = if (isLeftLayout) 1f else -1f
    Box(Proof.EntryIcon.toModifier().translateX((progress - 1f).times(layMul).times(100).percent)) {
        proof.icon()
    }
    Spacer()
    Column(
        modifier = Modifier
            .translateX((1f - progress).times(layMul).times(100).percent),
        horizontalAlignment = if (isLeftLayout) Alignment.End else Alignment.Start,
    ) {
        SpanText(
            proof.what,
            modifier = Proof.What.toModifier()
                .textAlign(if (isLeftLayout) TextAlign.Right else TextAlign.Left)
        )
        Span(
            attrs = Proof.Where.toModifier()
                .textAlign(if (isLeftLayout) TextAlign.Right else TextAlign.Left)
                .toAttrs()
        ) {
            FaAt(Proof.InlineIcon.toModifier().margin(right = SiteLayout.Space.XSmall))
            Text(proof.where)
        }
    }
}

@Composable
private fun BackgroundEntryProofSecondaryInfo(
    proof: BackgroundEntry.Proof,
    progress: Float,
) = Row(Proof.SecondaryInfo.toModifier().opacity(progress)) {
    proof.timeSpan?.let {
        LeadingIconRow(
            modifier = Modifier.translateX((progress - 1f).times(100).percent),
            icon = { FaCalendar(Proof.InlineIcon.toModifier()) },
        ) { SpanText(it) }
    }
    Spacer()
    proof.location?.let {
        TrailingIconRow(
            modifier = Modifier.translateX((1f - progress).times(100).percent),
            icon = { FaMapPin(Proof.InlineIcon.toModifier()) }
        ) { SpanText(it) }
    }
}

@Composable
private fun BackgroundEntryProofDescription(
    description: String,
    progress: Float,
) = SpanText(
    description,
    modifier = Proof.Description.toModifier()
        .opacity(progress)
        .translateY((1f - progress).times(100).percent)
)

@Composable
private fun BackgroundFacetScrollPrompt() = Column(
    modifier = BackgroundSectionStylesheet.ScrollPrompt.toModifier(),
    horizontalAlignment = Alignment.CenterHorizontally,
) {
    SpanText("Scroll to progress")
    HorizontalDivider(BackgroundSectionStylesheet.Divider.toModifier())
    FaChevronDown()
}

private fun buildSegmentsProgress(stepVH: Float, keepFullVH: Float) = buildList {
    var currentProgress = 0f
    BackgroundEntry.entries.forEach { entry ->
        val transitionFrac = entry.revealSteps * stepVH
        add(
            ProgressSegment(
                start = currentProgress,
                end = currentProgress + transitionFrac,
                entry = entry,
                direction = BackgroundEntryProgressDirection.FORWARD,
            )
        )
        add(
            ProgressSegment(
                start = currentProgress + transitionFrac,
                end = currentProgress + transitionFrac + keepFullVH,
                entry = entry,
                direction = BackgroundEntryProgressDirection.FULL,
            )
        )
        add(
            ProgressSegment(
                start = currentProgress + transitionFrac + keepFullVH,
                end = currentProgress + 1.5f * transitionFrac + keepFullVH,
                entry = entry,
                direction = BackgroundEntryProgressDirection.BACKWARD,
            )
        )
        currentProgress += 1.5f * transitionFrac + keepFullVH
    }
}
